import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Images, HeartHandshake } from "lucide-react";
import { GALLERY } from "@/lib/images";

const THANK_YOU_MESSAGE = "With heartfelt gratitude, our family extends sincere thanks to everyone who helped make Agnes Murphy's 107th birthday celebration so special. We are truly grateful to the many volunteers, family, friends, and supporters who donated their time, kindness, and generosity. Special thanks to the Italian Centre for generously donating the beautiful cake, Canadian Tire for providing the balloons, Catch A Limo for giving a beautiful ride to the church, Leon's for their generous donation of a beautiful upright chair, and the Royal Canadian Legion for arranging a veteran to attend — a meaningful tribute to Agnes's first husband who served. We also thank Ethan Dolan and Liam Dolan for personally donating and arranging the flowers with care. And with deep appreciation to Her Honour the Honourable Salma Lakhani, Lieutenant Governor of Alberta, for the official congratulatory birthday message. Every act of kindness helped create a celebration our family will cherish forever.";

export default function PhotoGallery() {
  const [lightbox, setLightbox] = useState(null);

  const prev = () => setLightbox((i) => (i - 1 + GALLERY.length) % GALLERY.length);
  const next = () => setLightbox((i) => (i + 1) % GALLERY.length);

  return (
    <section className="bg-cream py-24 md:py-32">
      <div className="mx-auto max-w-6xl px-5">
        <div className="mb-14 text-center">
          <Images className="mx-auto h-9 w-9 text-terracotta" />
          <p className="mt-4 text-xs uppercase tracking-[0.3em] text-terracotta">Through the Years</p>
          <h2 className="mt-3 font-serif text-4xl font-bold tracking-tight text-forest md:text-5xl">
            A Life in <span className="italic text-terracotta">Photos</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl leading-relaxed text-forest/65">
            Moments captured across the decades — Agnes laughing, celebrating, and inspiring everyone around her.
          </p>
        </div>

        <motion.article
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.65 }}
          className="relative mb-14 overflow-hidden rounded-2xl border border-gold/40 bg-[#f8f1e5] px-6 py-10 text-center shadow-[0_18px_45px_-28px_rgba(31,57,43,0.65)] md:px-16 md:py-12"
        >
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-gold to-transparent" />
          <HeartHandshake className="mx-auto h-9 w-9 text-terracotta" />
          <p className="mt-4 text-xs uppercase tracking-[0.3em] text-terracotta">A Note of Thanks</p>
          <h3 className="mt-3 font-serif text-3xl font-bold text-forest md:text-4xl">
            With Heartfelt <span className="italic text-terracotta">Gratitude</span>
          </h3>
          <div className="mx-auto mt-6 max-w-4xl space-y-4 text-left font-serif leading-relaxed text-forest/80 md:text-center">
            <p>{THANK_YOU_MESSAGE}</p>
            <p className="pt-2 font-hand text-2xl text-terracotta">
              With sincere appreciation,<br />
              the Murphy and Dolan family
            </p>
          </div>
        </motion.article>

        <div className="columns-2 gap-4 sm:columns-3 lg:columns-4">
          {GALLERY.map((photo, i) => (
            <motion.div
              key={photo.src}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: (i % 4) * 0.08 }}
              className="group mb-4 cursor-pointer break-inside-avoid"
              onClick={() => setLightbox(i)}
            >
              <div className="relative overflow-hidden rounded-xl shadow-md transition-all duration-300 group-hover:shadow-xl group-hover:scale-[1.02]">
                <img
                  src={photo.src}
                  alt={photo.caption}
                  className="w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-forest/70 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 p-4">
                  <p className="font-hand text-lg text-cream leading-tight">{photo.caption}</p>
                  <p className="text-xs text-gold tracking-widest uppercase mt-1">{photo.year}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
            onClick={() => setLightbox(null)}
          >
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white hover:bg-white/25 transition"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 rounded-full bg-white/10 p-3 text-white hover:bg-white/25 transition"
              aria-label="Previous"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            <motion.div
              key={lightbox}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="max-h-[85vh] max-w-3xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY[lightbox].src}
                alt={GALLERY[lightbox].caption}
                className="max-h-[75vh] w-full rounded-lg object-contain shadow-2xl"
              />
              <div className="mt-4 text-center">
                <p className="font-hand text-2xl text-cream">{GALLERY[lightbox].caption}</p>
                <p className="mt-1 text-xs uppercase tracking-widest text-gold">{GALLERY[lightbox].year}</p>
              </div>
            </motion.div>

            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 rounded-full bg-white/10 p-3 text-white hover:bg-white/25 transition"
              aria-label="Next"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
